import React, { ReactElement, useCallback, useRef, useState } from 'react';

import {
    ListRenderItemInfo,
    Platform,
    Text,
    TouchableOpacity,
    View,
} from 'react-native';

import { useTranslation } from 'react-i18next';
import { FlatList } from 'react-native-gesture-handler';

import StripeArrow from 'assets/images/svgs/ico_arrow_right_season.svg';

import {
    DETAIL_SEASON_SELECTOR,
    DETAIL_SEASON_TAB_ID,
    DETAIL_SEASON_TAB_OTHER_ID,
} from 'config/testID';

import { lightGrey } from 'utils/colors';
import { isTablet, isWeb, widthPercentageToDP } from 'utils/responsiveness';

import {
    SCREEN_DETAIL_PROGRAM_SEASON_TAB_OTHER_EPISODE,
    SCREEN_DETAIL_SEASONS_TAB_SELECTOR,
    SCREEN_DETAIL_SEASON_TAB_SELECTOR,
} from 'screens/Detail/languageConst';

import SvgButton from 'components/CustomButtons/SvgButton';
import LocalizedText from 'components/LocalizedText';

import { noSeasonName } from '../../constants';
import { SeasonListItem } from '../../types.ts';
import { ISeasonsSelectorNarrow } from '../SeasonBottomSheet';
import styles from './styles';

const SEASON_SELECTOR_WIDE_WIDTH = Platform.select({
    web: 360,
    default: widthPercentageToDP(480, 360),
});
export const SEASON_BUTTON_SIZE = 40;

export const SeasonsSelectorWide = (
    props: ISeasonsSelectorNarrow,
): ReactElement => {
    const { list, selected, onSelect } = props;
    const [isScrollEnabled, setIsScrollEnabled] = useState<boolean>(false);
    const [shouldEnableFwdArrow, setShouldEnableFwdArrow] = useState(false);
    const [shouldEnableBwdArrow, setShouldEnableBwdArrow] = useState(false);

    const { t } = useTranslation();

    const hasOneSeason = list.length === 1;

    const ARROW_ICON_SIZE = {
        width: 16,
        height: 24,
    };

    const handleSelect = useCallback(
        (item) => () => {
            onSelect(item);
        },
        [onSelect],
    );

    const renderItem = useCallback(
        ({ item }: ListRenderItemInfo<SeasonListItem>) => {
            const hasNoSeasonName = item.seasonId === noSeasonName;
            const isSelected = selected.seasonId === item.seasonId;

            return (
                <TouchableOpacity
                    testID={
                        hasNoSeasonName
                            ? DETAIL_SEASON_TAB_OTHER_ID
                            : `${DETAIL_SEASON_TAB_ID}_${item}`
                    }
                    key={item.seasonId}
                    style={[
                        styles.listItemContainer,
                        isSelected && styles.listItemContainerSelected,
                        hasNoSeasonName && {
                            width: 'auto',
                            paddingHorizontal: 16,
                        },
                    ]}
                    onPress={handleSelect(item)}
                    disabled={hasOneSeason}
                    accessibilityLabel={`${t(
                        SCREEN_DETAIL_SEASON_TAB_SELECTOR,
                    )} ${
                        hasNoSeasonName
                            ? t(SCREEN_DETAIL_PROGRAM_SEASON_TAB_OTHER_EPISODE)
                            : item
                    }`}
                    accessibilityRole="button"
                >
                    <Text
                        style={[
                            styles.listItemText,
                            !isSelected && styles.listItemTextSelected,
                        ]}
                        numberOfLines={2}
                    >
                        {/*
                        @ts-ignore */}
                        {hasNoSeasonName ? (
                            <LocalizedText
                                numberOfLines={2}
                                textKey={
                                    SCREEN_DETAIL_PROGRAM_SEASON_TAB_OTHER_EPISODE
                                }
                            />
                        ) : (
                            item.seasonNumber
                        )}
                    </Text>
                </TouchableOpacity>
            );
        },
        [selected, handleSelect, hasOneSeason, t],
    );

    const currentSeasonOffset = useRef(0);
    const flatListRef = useRef<any>(null);
    const SCROLL_OFFSET_SEASONS = 3;

    const onContentSizeChange = useCallback(
        (width) => {
            const selectedIndex = list.findIndex((item) => item === selected);
            const selectedIndexWithFallback =
                selectedIndex >= 0 ? selectedIndex : 0;

            if (width > SEASON_SELECTOR_WIDE_WIDTH) {
                setIsScrollEnabled(true);
                setShouldEnableFwdArrow(selectedIndex <= list.length);
            }

            if (selectedIndex !== -1) {
                setTimeout(() => {
                    flatListRef.current?.scrollToIndex({
                        index: selectedIndexWithFallback,
                        animated: true,
                    });
                }, 500);
                currentSeasonOffset.current = selectedIndexWithFallback - 3;
                setShouldEnableBwdArrow(true);

                if (selectedIndex - 3 <= list.length) {
                    setShouldEnableFwdArrow(false);
                }
            }
        },
        [list, selected],
    );

    const onScrollForward = useCallback(() => {
        if (shouldEnableFwdArrow) {
            if (
                currentSeasonOffset.current + SCROLL_OFFSET_SEASONS <
                list.length
            ) {
                flatListRef.current?.scrollToIndex({
                    index: currentSeasonOffset.current + SCROLL_OFFSET_SEASONS,
                    animated: true,
                });
                currentSeasonOffset.current += SCROLL_OFFSET_SEASONS;
            } else {
                flatListRef.current?.scrollToIndex({
                    index: list.length - 1,
                    animated: true,
                });
                currentSeasonOffset.current = list.length - 1;
            }
            setShouldEnableBwdArrow(true);
        }
    }, [shouldEnableFwdArrow, list.length]);

    const onScrollBackwards = useCallback(() => {
        if (shouldEnableBwdArrow) {
            if (currentSeasonOffset.current >= SCROLL_OFFSET_SEASONS) {
                flatListRef.current?.scrollToIndex({
                    index: currentSeasonOffset.current - SCROLL_OFFSET_SEASONS,
                    animated: true,
                });
                currentSeasonOffset.current -= SCROLL_OFFSET_SEASONS;
            } else {
                flatListRef.current?.scrollToIndex({
                    index: 0,
                    animated: true,
                });
                currentSeasonOffset.current = 0;
            }
        }

        if (!shouldEnableFwdArrow) {
            setShouldEnableFwdArrow(true);
        }

        if (currentSeasonOffset.current === 0) {
            setShouldEnableBwdArrow(false);
        }
    }, [
        flatListRef,
        shouldEnableBwdArrow,
        currentSeasonOffset,
        shouldEnableFwdArrow,
    ]);

    const onEndReached = useCallback(() => {
        setShouldEnableFwdArrow(false);
    }, []);

    const onScrollToIndexFailed = useCallback(() => {
        flatListRef.current.scrollToIndex(0);
    }, []);

    const getItemLayout = useCallback(
        (_, index) => ({
            length: 40,
            offset: index * 40,
            index,
        }),
        [],
    );

    const renderFlatList = useCallback(() => {
        return (
            <FlatList
                ref={flatListRef}
                horizontal
                nestedScrollEnabled
                style={[
                    styles.flatList,
                    isTablet && isScrollEnabled && { marginLeft: 20 },
                ]}
                scrollEnabled={isScrollEnabled}
                initialScrollIndex={0}
                data={list}
                snapToAlignment="start"
                decelerationRate="fast"
                keyExtractor={(item, index) => {
                    return `${item || 'noId'}${index}`;
                }}
                renderItem={renderItem}
                showsHorizontalScrollIndicator={false}
                onContentSizeChange={onContentSizeChange}
                onEndReached={onEndReached}
                onScrollToIndexFailed={onScrollToIndexFailed}
                getItemLayout={getItemLayout}
            />
        );
    }, [
        isScrollEnabled,
        list,
        renderItem,
        getItemLayout,
        onScrollToIndexFailed,
        onContentSizeChange,
        onEndReached,
    ]);

    return (
        <View style={styles.container} testID={DETAIL_SEASON_SELECTOR}>
            <LocalizedText
                style={styles.buttonText}
                textKey={
                    hasOneSeason
                        ? SCREEN_DETAIL_SEASON_TAB_SELECTOR
                        : SCREEN_DETAIL_SEASONS_TAB_SELECTOR
                }
            />

            {isTablet && (
                <View style={styles.listContainer}>{renderFlatList()}</View>
            )}

            {isWeb && (
                <>
                    {isScrollEnabled && (
                        <SvgButton
                            style={[
                                styles.arrowButton,
                                {
                                    transform: [{ rotate: '180deg' }],
                                    fill: shouldEnableBwdArrow
                                        ? lightGrey(1)
                                        : lightGrey(0.5),
                                    marginLeft: 29,
                                },
                            ]}
                            Svg={StripeArrow}
                            svgStyle={ARROW_ICON_SIZE}
                            onPress={onScrollBackwards}
                            disabled={!shouldEnableBwdArrow}
                        />
                    )}
                    {renderFlatList()}
                    {isScrollEnabled && (
                        <SvgButton
                            style={[
                                styles.arrowButton,
                                {
                                    fill: shouldEnableFwdArrow
                                        ? lightGrey(1)
                                        : lightGrey(0.5),
                                },
                            ]}
                            Svg={StripeArrow}
                            svgStyle={ARROW_ICON_SIZE}
                            onPress={onScrollForward}
                            disabled={!shouldEnableFwdArrow}
                        />
                    )}
                </>
            )}
        </View>
    );
};
